import XavierNavGroup from "./XavierNavGroup";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FiBookOpen,
  FiDollarSign,
  FiFileText,
  FiGrid,
  FiLogOut,
  FiMenu,
  FiMoon,
  FiSettings,
  FiSun,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { GiScales } from "react-icons/gi";
import MenuConfig from "../../../_metronic/layout/MenuConfig";
import UserProfile from "../../partials/layout/UserProfile";
import { useXavierTheme } from "../theme/XavierThemeProvider";
import XavierWordmark from "../components/XavierWordmark/XavierWordmark";
import NotificationCenter from "./NotificationCenter";
import "./XavierLayout.css";
import "../theme/LegacyBridge.css";

const ICONS = {
  cash: FiDollarSign,
  adjustments: FiSettings,
  dashboard: FiGrid,
  document: FiFileText,
  lawsuit: GiScales,
  "users-profile": FiUsers,
};

const MENU_LABELS = {
  ASSISTENTE: "Assistente",
  DASHBOARD: "Início",
  DOCUMENTOS: "Documentos",
  LOGS: "Logs",
  "PARAMETRIZAÇŌES": "Parametrizações",
  "PENDÊNCIAS DOC.": "Pendências documentais",
  PROCESSOS: "Processos",
  "PUBLICAÇÕES": "Publicações",
  "USUÁRIOS": "Usuários",
};

const ROLE_LABELS = {
  ADMIN: "Administrador",
  COLLABORATOR: "Colaborador",
  CLIENT: "Cliente",
};

const ROUTE_TITLES = [
  ["/admin/financeiro/envios", "Envios financeiros", "Fila, resultados e bloqueios dos envios registrados"],
  ["/admin/financeiro/ligacoes", "Ligações e custos", "Histórico de ligações e custos dos provedores"],
  ["/admin/parametrizacao/whatsapp", "Conexões WhatsApp", "Gerencie a conexão do WhatsApp do escritório"],
  ["/admin/financeiro/conferencia", "Conferência de cadastros", "Confira os dados do ClickUp e os vínculos com o Asaas"],
  ["/admin/financeiro/revisao", "Revisão financeira", "Confira conteúdo e evidências das cobranças"],
  ["/admin/meu-financeiro", "Meu financeiro", "Valores, vencimentos e pagamentos"],
  ["/admin/financeiro/cobrancas", "Central de cobranças", "Consulte o Asaas e prepare o contato com cada pagador"],
  ["/admin/financeiro", "Financeiro", "Indicadores e cobranças da conta Asaas"],
  ["/admin/usuario/adicionar", "Adicionar usuário", "Cadastre um novo acesso ao portal"],
  ["/admin/usuario/", "Usuário", "Consulte e atualize os dados do usuário"],
  ["/admin/usuario", "Usuários", "Gestão de usuários e permissões de acesso"],
  ["/admin/cliente/adicionar", "Adicionar cliente", "Cadastre um novo cliente no portal"],
  ["/admin/cliente/", "Cliente", "Consulte e atualize os dados do cliente"],
  ["/admin/processo/adicionar", "Adicionar processo", "Cadastre um novo processo jurídico"],
  ["/admin/processo/", "Processo", "Informações e documentos do processo"],
  ["/admin/processo", "Processos", "Acompanhe todos os processos jurídicos em andamento"],
  ["/admin/acompanhamentoDocumentos", "Documentos", "Gestão e acompanhamento de documentos dos processos"],
  ["/admin/cadencia-documental", "Pendências documentais", "Solicitações, prazos e acompanhamento de documentos"],
  ["/admin/parametrizacao", "Parametrizações", "Configurações operacionais do portal"],
  ["/admin/publicacoes", "Publicações", "Publicações e movimentações processuais"],
  ["/admin/assistente", "Assistente", "Atendimentos, conversas e transferências para a equipe"],
  ["/admin/dashboard", "Dashboard", "Indicadores e atividades do escritório"],
  ["/admin/logs", "Logs", "Histórico de execuções e eventos do sistema"],
];

const formatDocument = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 14) {
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  }
  return value;
};

export const getPageMeta = (pathname) => {
  const match = ROUTE_TITLES.find(([route]) => pathname.startsWith(route));
  return {
    title: match?.[1] || "Portal",
    subtitle: match?.[2] || "Portal do cliente Xavier Advocacia",
  };
};

const formatLastAccess = (value) => {
  if (!value) return "Acesso atual";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Acesso atual";

  return `${date.toLocaleDateString("pt-BR")} — ${date.toLocaleTimeString(
    "pt-BR",
    { hour: "2-digit", minute: "2-digit" }
  )}`;
};

export default function XavierLayout({ children }) {
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const { mode, toggleMode } = useXavierTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pageMeta = getPageMeta(location.pathname);
  const isDashboard = location.pathname === "/admin/dashboard";
  const pageTitle = pageMeta.title;
  const items = MenuConfig().aside.items;
  const userName = (user?.name || user?.username || "Usuário").toLocaleUpperCase(
    "pt-BR"
  );
  const userRole = ROLE_LABELS[user?.role] || "Usuário";
  const userDocument = formatDocument(user?.cpfCnpj);
  const lastAccess = formatLastAccess(
    user?.lastAccessAt ||
      user?.lastLoginAt ||
      localStorage.getItem("xavier-portal:previous-access-at") ||
      localStorage.getItem("xavier-portal:current-access-at")
  );

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="xv-layout">
      <button
        type="button"
        className={`xv-sidebar-backdrop ${mobileMenuOpen ? "is-visible" : ""}`}
        aria-label="Fechar menu"
        onClick={() => setMobileMenuOpen(false)}
      />

      <aside className={`xv-sidebar ${mobileMenuOpen ? "is-open" : ""}`}>
        <div className="xv-sidebar__brand">
          <Link to="/admin" aria-label="Ir para o início do Portal Xavier">
            <XavierWordmark compact />
          </Link>
          <button
            type="button"
            className="xv-icon-button xv-sidebar__close"
            aria-label="Fechar menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            <FiX />
          </button>
        </div>

        <div className="xv-client-card">
          <span className="xv-label-uppercase">Acesso ativo</span>
          <strong>{userName}</strong>
          <small className="xv-client-card__role">
            {userRole.toLocaleUpperCase("pt-BR")}
          </small>
          {userDocument && <small>{userDocument}</small>}
        </div>

        <nav className="xv-nav" aria-label="Navegação principal">
          <span className="xv-label-uppercase xv-nav__label">Acesso</span>
          {items.map((item) => {
            const Icon = ICONS[item.icon] || FiBookOpen;
            const target = `/${item.page}`;
            const active = location.pathname === target || location.pathname.startsWith(`${target}/`);
            if (item.submenu) return <XavierNavGroup key={item.page} item={item} Icon={Icon} pathname={location.pathname} />;
            return (
              <Link
                className={`xv-nav-item ${active ? "is-active" : ""}`}
                key={item.page || item.title}
                to={target}
                aria-current={active ? "page" : undefined}
              >
                <Icon aria-hidden="true" strokeWidth={1.7} />
                <span>{MENU_LABELS[item.title] || item.title}</span>
                {item.badge ? (
                  <span className="xv-nav-badge">{item.badge}</span>
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="xv-sidebar__footer">
          <div className="xv-secure-status">
            <span aria-hidden="true" />
            Ambiente seguro e atualizado
          </div>
          <Link className="xv-nav-item" to="/logout">
            <FiLogOut aria-hidden="true" />
            <span>Sair do portal</span>
          </Link>
        </div>
      </aside>

      <div className="xv-main">
        <header
          className={`xv-header ${
            isDashboard ? "xv-header--dashboard" : "xv-header--editorial"
          }`}
        >
          <div className="xv-header__identity">
            <button
              type="button"
              className="xv-icon-button xv-mobile-menu"
              aria-label="Abrir menu"
              onClick={() => setMobileMenuOpen(true)}
            >
              <FiMenu />
            </button>
            <div>
              <span className="xv-header__breadcrumb">
                Portal do cliente / Xavier Advocacia
              </span>
              <strong
                className={`xv-header__page-title ${
                  isDashboard
                    ? "xv-header__page-title--dashboard"
                    : ""
                }`}
              >
                {isDashboard ? "Início" : pageTitle}
              </strong>
              {!isDashboard && (
                <>
                  <small className="xv-header__page-subtitle">
                    {pageMeta.subtitle}
                  </small>
                </>
              )}
            </div>
          </div>

          <div className="xv-header__actions">
            <button
              type="button"
              className="xv-icon-button"
              aria-label={
                mode === "dark"
                  ? "Mudar para tema claro"
                  : "Mudar para tema escuro"
              }
              onClick={toggleMode}
            >
              {mode === "dark" ? <FiSun /> : <FiMoon />}
            </button>
            <NotificationCenter pathname={location.pathname} />
            <div className="xv-header__divider" />
            <div className="xv-last-access">
              <span className="xv-label-uppercase">Último acesso</span>
              <strong>{lastAccess}</strong>
            </div>
            <UserProfile showBadge />
          </div>
        </header>

        <main className="xv-content">
          {children}
        </main>
      </div>
    </div>
  );
}
